import * as React from "react";
import styles from "./StatusBadge.module.css";

export interface StatusBadgeProps {
  /** The label text to display */
  label: string;
  /**
   * Text colour for a label that is not a status word. A status label
   * (see `statusTone`) ignores it and takes the status pill colours.
   */
  color?: string;
  /**
   * Background for a label that is not a status word. A status label
   * ignores it and takes the status pill colours.
   */
  background?: string;
  /** Additional className for the badge */
  className?: string;
}

type StatusTone =
  | "running"
  | "help"
  | "done"
  | "pending"
  | "stopped"
  | "fault"
  | "offline";

/*
 * Status words, grouped by pill (design system README, "Status"). The
 * keys are the label in lower case, with "_" as a space, and with a
 * trailing "..." or "(...)" removed. For example, "Scheduled (3d)" and
 * "Starting..." match "scheduled" and "starting".
 */
const STATUS_TONES: Record<string, StatusTone> = {
  // Running, active, live, in progress.
  running: "running",
  active: "running",
  live: "running",
  online: "running",
  recording: "running",
  "in progress": "running",
  started: "running",
  producing: "running",
  managed: "running",
  // Needs an operator.
  "needs help": "help",
  "needs review": "help",
  "waiting for operator": "help",
  // Done. Green is for running, not for done.
  succeeded: "done",
  success: "done",
  completed: "done",
  complete: "done",
  done: "done",
  ready: "done",
  passed: "done",
  finished: "done",
  // Queued, pending, or in a transition.
  queued: "pending",
  pending: "pending",
  teleop: "pending",
  warning: "pending",
  waiting: "pending",
  "waiting for gpu": "pending",
  "waiting for availability": "pending",
  scheduled: "pending",
  "not started": "pending",
  starting: "pending",
  launching: "pending",
  "launching instance": "pending",
  provisioning: "pending",
  "payment processing": "pending",
  canceling: "pending",
  cancelling: "pending",
  stopping: "pending",
  terminating: "pending",
  "shutting down": "pending",
  // Stopped or inactive.
  stopped: "stopped",
  cancelled: "stopped",
  canceled: "stopped",
  paused: "stopped",
  terminated: "stopped",
  skipped: "stopped",
  archived: "stopped",
  expired: "stopped",
  draft: "stopped",
  // Fault.
  failed: "fault",
  failure: "fault",
  error: "fault",
  errored: "fault",
  fault: "fault",
  crashed: "fault",
  "payment failed": "fault",
  // Offline.
  offline: "offline",
  unknown: "offline",
  disconnected: "offline",
};

const TONE_CLASS: Record<StatusTone, string> = {
  running: styles.running,
  help: styles.help,
  done: styles.done,
  pending: styles.pending,
  stopped: styles.stopped,
  fault: styles.fault,
  offline: styles.offline,
};

/** Returns the pill for a status label, or null for any other label. */
function statusTone(label: string): StatusTone | null {
  const key = label
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\s*\(.*\)\s*$/, "")
    .replace(/(\.\.\.|…)\s*$/, "")
    .trim();
  return STATUS_TONES[key] ?? null;
}

/* Acronyms that stay in capitals in a sentence-case label. */
const ACRONYMS = new Set([
  "ami",
  "api",
  "aws",
  "cpu",
  "ddp",
  "ec2",
  "gcp",
  "gpu",
  "id",
  "iql",
  "sft",
  "sop",
  "sql",
  "ssh",
  "sxm",
  "url",
]);

/* Words with a fixed spelling in a sentence-case label. */
const SPECIAL_WORDS: Record<string, string> = {
  lerobot: "LeRobot",
};

/* An enum token in capitals: one or more words of capitals and digits,
   joined by "_" ("RUNNING", "NOT_STARTED", "LEROBOT_DATASET"). */
const CAPS_ENUM = /^[A-Z0-9]+(_[A-Z0-9]+)*$/;
/* A snake_case token ("waiting_for_gpu"). It changes only when it is a
   status word, so a data value such as a tag ("small_towel") shows as the
   caller gives it. */
const SNAKE_ENUM = /^[A-Za-z0-9]+(_[A-Za-z0-9]+)+$/;

/** Returns one word of an enum token in sentence-case form. */
function displayWord(word: string): string {
  const lower = word.toLowerCase();
  if (SPECIAL_WORDS[lower]) return SPECIAL_WORDS[lower];
  if (ACRONYMS.has(lower)) return word.toUpperCase();
  // A word with a digit is a name ("H100", "8X"). It keeps its case.
  if (/[0-9]/.test(word)) return word;
  return lower;
}

/**
 * Returns the text to show for a label. An enum token in capitals, or a
 * snake_case status word, shows in sentence case: "RUNNING" as "Running",
 * "NOT_STARTED" as "Not started", "waiting_for_gpu" as "Waiting for GPU",
 * "LEROBOT_DATASET" as "LeRobot dataset". Every other label shows as the
 * caller gives it, for example "DDP Parity (8x H100)", "Payment
 * processing" or the tag "small_towel".
 */
function displayLabel(label: string): string {
  const isEnum =
    CAPS_ENUM.test(label) ||
    (SNAKE_ENUM.test(label) && statusTone(label) !== null);
  if (!isEnum) {
    return label;
  }
  const text = label.split("_").map(displayWord).join(" ");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * A status pill: a dot and a word (design system README, "Status"). The
 * label sets the pill colours, so every status looks the same in every
 * package and in both themes. A label that is not a status word shows as
 * a neutral badge with no dot.
 *
 * Usage:
 * ```tsx
 * <StatusBadge label="Running" />
 * <StatusBadge label="latest" />
 * ```
 */
export function StatusBadge({ label, color, background, className }: StatusBadgeProps) {
  const tone = statusTone(label);
  const combinedClass = [
    styles.badge,
    tone ? styles.status : undefined,
    tone ? TONE_CLASS[tone] : undefined,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <span
      className={combinedClass}
      style={!tone && (color || background) ? { color, background } : undefined}
    >
      {displayLabel(label)}
    </span>
  );
}
